import { config } from "@config";
import { ElementRef, Injectable } from "@angular/core";
import { TranslationService } from "@core-services/translation.service";
import { CustomModal } from "../../core/components/base-modals";
import { Angulartics2GoogleTagManager } from "angulartics2";
import { Script } from "@models/script";
import { ScriptManagerService } from "@core-services/script-manager.service";
import { BehaviorSubject, Observable } from "rxjs";
import { take } from "rxjs/operators";

type GtagConsentValue = "denied" | "granted";

declare global {
	interface Window {
		gtag?: (
			command: "consent",
			type: "update" | "default",
			value: Partial<{
				ad_storage: GtagConsentValue;
				ad_user_data: GtagConsentValue;
				ad_personalization: GtagConsentValue;
				analytics_storage: GtagConsentValue;
				functionality_storage: GtagConsentValue;
				personalization_storage: GtagConsentValue;
				security_storage: GtagConsentValue;
			}>
		) => void;
	}
}

@Injectable({
	providedIn: "root"
})
export class CookieBannerService {
	private state: BehaviorSubject<CookieBannerState> = new BehaviorSubject<CookieBannerState>(new CookieBannerState());

	private _cookieBannerComponent: ElementRef;

	private googleAnalyticsScriptNode; // The DOM node <script>
	private googleTagManagerScriptNode; // The DOM node <script>
	private googleTagManagerIFrameScriptNode; // The DOM node <script>
	private gtmDefaultSettingsScriptNode; // Fixes undefined gtm console errors

	private refreshAfterAccept: boolean = false; // If true, this reloads the current route, after user accepted cookies

	public constructor(
		private translationService: TranslationService,
		private scriptManagerService: ScriptManagerService
	) {}

	public set cookieBannerComponent(elem: ElementRef) {
		this._cookieBannerComponent = elem;
	}

	public get cookieBannerComponent(): ElementRef {
		return this._cookieBannerComponent;
	}

	public forceShowPopup(): void {
		const component = this.cookieBannerComponent.nativeElement;
		component.language = TranslationService.getLanguageIsoCode().toLowerCase();
		component.showPopup();
		this.setVisibility(true);
		this.refreshAfterAccept = true;
	}

	public setVisibility(value: boolean): void {
		const s = this.getState();
		s.setVisibility(value);
		this.setState(s);
		this.checkState();
	}

	// Wait for Stencil web component to finish loading:

	public componentLoaded(): void {
		this.translationService
			.languageUpdate$()
			.pipe(take(2))
			.subscribe((lang) => {
				const component = this.cookieBannerComponent.nativeElement;
				component.language = TranslationService.getLanguageIsoCode(lang).toLowerCase();
			});
	}

	public userAcceptedTerms(): void {
		// If Google Tag Manager was already injected into website, it needs a page reload to be removed
		if (this.refreshAfterAccept) {
			this.refreshAfterAccept = false;
			location.reload();
		}
	}

	// Check what cookie groups should be Enabled / Disabled:

	private checkState(): void {
		if (!this.cookieBannerComponent || !this.cookieBannerComponent.nativeElement) {
			return;
		}

		this.cookieBannerComponent.nativeElement.getAcceptedPerformance().then((accepted) => {
			const s = this.getState();
			s.setPerformance(accepted);
			this.setState(s);
			accepted ? this.enablePerformanceCookies() : this.disablePerformanceCookies();
		});

		this.cookieBannerComponent.nativeElement.getAcceptedAdvertisement().then((accepted) => {
			const s = this.getState();
			s.setAdvertisement(accepted);
			this.setState(s);
			accepted ? this.enableAdvertisementCookies() : this.disableAdvertisementCookies();
		});

		this.cookieBannerComponent.nativeElement.getAcceptedEssentials().then((accepted) => {
			const s = this.getState();
			s.setEssentials(accepted);
			this.setState(s);
		});
	}

	// Performance cookies Group

	private enablePerformanceCookies(): void {
		this.setPerformanceGoogleConsent("granted");
		this.enableGoogleAnalytics();
		this.enableGoogleTagManager();
		this.enableSegment();
		this.enableRudderstack();
	}

	private disablePerformanceCookies(): void {
		this.setPerformanceGoogleConsent("denied");
		this.disableGoogleAnalytics();
		this.disableGoogleTagManager();
		this.disableSegment();
		this.disableRudderstack();
	}

	private setPerformanceGoogleConsent(state: GtagConsentValue): void {
		if (window.gtag) {
			window.gtag("consent", "update", {
				analytics_storage: state
			});
		}
	}

	private setAdvertisementGoogleConsent(state: GtagConsentValue): void {
		if (window.gtag) {
			window.gtag("consent", "update", {
				ad_storage: state,
				ad_user_data: state,
				ad_personalization: state,
				personalization_storage: state
			});
		}
	}

	/*
	 *	Google Analytics:
	 */

	// Enable
	private enableGoogleAnalytics(): void {
		const scriptToAdd: string = `window.ga = window.ga || function () {
                (ga.q = ga.q || []).push(arguments)
            };
            ga.l = +new Date;
            ga('create', 'UA-55274179-2', 'auto');
            ga('set', 'anonymizeIp', true);
            ga(function(tracker) {
				window.gaId = tracker.get('clientId');
			});`;

		this.createScriptElementDOM(this.googleAnalyticsScriptNode, scriptToAdd);
	}

	// Disable
	private disableGoogleAnalytics(): void {
		this.removeScriptElementDOM(this.googleAnalyticsScriptNode);
	}

	/*
	 *	Google Tag Manager:
	 */

	// Enable
	private enableGoogleTagManager(): void {
		// Default settings
		const settingsScript: string = `var google_conversion_value = 0; var google_conversion_currency = "EUR";`;
		this.createScriptElementDOM(this.gtmDefaultSettingsScriptNode, settingsScript);

		// Script
		const scriptToAdd: string = `startGoogleTagManager(window, document, 'script', 'dataLayer', 'GTM-PCNBH5L');`;
		this.createScriptElementDOM(this.googleTagManagerScriptNode, scriptToAdd);

		// Iframe noscript for blocking JS websites
		const scriptToAddIFrame: string = `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-PCNBH5L" height="0" width="0" style="display:none;visibility:hidden"></iframe>;`;
		this.createScriptElementDOM(this.googleTagManagerIFrameScriptNode, scriptToAddIFrame, "noscript", "body");
	}

	// Disable
	private disableGoogleTagManager(): void {
		this.removeScriptElementDOM(this.gtmDefaultSettingsScriptNode);
		this.removeScriptElementDOM(this.googleTagManagerScriptNode);
		this.removeScriptElementDOM(this.googleTagManagerIFrameScriptNode, "body");
	}

	/*
	 *	Segment
	 */

	// Enable
	private enableSegment(): void {
		const scriptToAdd: string = `!(function () {
			var analytics = (window.analytics = window.analytics || []);
			if (!analytics.initialize)
				if (analytics.invoked)
				window.console &&
					console.error &&
					console.error("Segment snippet included twice.");
				else {
				analytics.invoked = !0;
				analytics.methods = [
					"trackSubmit",
					"trackClick",
					"trackLink",
					"trackForm",
					"pageview",
					"identify",
					"reset",
					"group",
					"track",
					"ready",
					"alias",
					"debug",
					"page",
					"once",
					"off",
					"on",
				];
				analytics.factory = function (t) {
					return function () {
					var e = Array.prototype.slice.call(arguments);
					e.unshift(t);
					analytics.push(e);
					return analytics;
					};
				};
				for (var t = 0; t < analytics.methods.length; t++) {
					var e = analytics.methods[t];
					analytics[e] = analytics.factory(e);
				}
				analytics.load = function (t, e) {
					var n = document.createElement("script");
					n.type = "text/javascript";
					n.async = !0;
					n.src =
					"https://cdn-segment.bitpanda.com/analytics.js/v1/" +
					t +
					"/analytics.min.js";
					var a = document.getElementsByTagName("script")[0];
					a.parentNode.insertBefore(n, a);
					analytics._loadOptions = e;
				};
				analytics.SNIPPET_VERSION = "4.13.2";
				analytics.load("${config.segment["writeKey"]}", { 
					integrations: { 
						'Segment.io' : {
							deliveryStrategy: {
								strategy: 'batching',
								config: {
									size: 15,
									timeout: 2000
								}
							}
						} 
					}  
				});
				// analytics.page() // Uncomment if your application is NOT an SPA
				}
			})();`;
		const segmentScript: Script = this.scriptManagerService.createScript("segmentScript", scriptToAdd);
		this.scriptManagerService.loadScript(segmentScript);
	}

	// Disable
	private disableSegment(): void {
		this.scriptManagerService.removeScriptByName("segmentScript");
	}

	/*
	 *	Rudderstack
	 */

	rudderSnippetVersion = "3.2.0";
	sdkVersion = "v3";

	// Enable
	private enableRudderstack(): void {
		const scriptToAdd: string = `(function() {
		  "use strict";
		  window.RudderSnippetVersion = '${this.rudderSnippetVersion}';
		  var identifier = "rudderanalytics";
		  if (!window[identifier]) {
			window[identifier] = [];
		  }
		  var rudderanalytics = window[identifier];
		  if (Array.isArray(rudderanalytics)) {
			if (rudderanalytics.snippetExecuted === true && window.console && console.error) {
			  console.error("RudderStack JavaScript SDK snippet included more than once.");
			} else {
			  rudderanalytics.snippetExecuted = true;
			  window.rudderAnalyticsBuildType = "legacy";
			  var sdkBaseUrl = "https://cdn.rudderlabs.com";
			  var sdkVersion = '${this.sdkVersion}';
			  var sdkFileName = "rsa.min.js";
			  var scriptLoadingMode = "async";
			  var methods = [ 
			  	"setDefaultInstanceKey", 
			  	"load", 
			  	"ready", 
			  	"page", 
			  	"track", 
			  	"identify", 
			  	"alias", 
			  	"group", 
			  	"reset", 
			  	"setAnonymousId", 
			  	"startSession", 
			  	"endSession", 
			  	"consent", 
			  	"addCustomIntegration" 
			  ];
			  for (var i = 0; i < methods.length; i++) {
				var method = methods[i];
				rudderanalytics[method] = function(methodName) {
				  return function() {
					if (Array.isArray(window[identifier])) {
					  rudderanalytics.push([ methodName ].concat(Array.prototype.slice.call(arguments)));
					} else {
					  var _methodName;
					  (_methodName = window[identifier][methodName]) === null || _methodName === undefined || _methodName.apply(window[identifier], arguments);
					}
				  };
				}(method);
			  }
			  try {
				new Function('class Test{field=()=>{};test({prop=[]}={}){return prop?(prop?.property??[...prop]):import("");}}');
				window.rudderAnalyticsBuildType = "modern";
			  } catch (e) {}
			  var head = document.head || document.getElementsByTagName("head")[0];
			  var body = document.body || document.getElementsByTagName("body")[0];
			  window.rudderAnalyticsAddScript = function(url, extraAttributeKey, extraAttributeVal) {
				var scriptTag = document.createElement("script");
				scriptTag.src = url;
				scriptTag.setAttribute("data-loader", "RS_JS_SDK");
				if (extraAttributeKey && extraAttributeVal) {
				  scriptTag.setAttribute(extraAttributeKey, extraAttributeVal);
				}
				if (scriptLoadingMode === "async") {
				  scriptTag.async = true;
				} else if (scriptLoadingMode === "defer") {
				  scriptTag.defer = true;
				}
				if (head) {
				  head.insertBefore(scriptTag, head.firstChild);
				} else {
				  body.insertBefore(scriptTag, body.firstChild);
				}
			  };
			  window.rudderAnalyticsMount = function() {
				(function() {
				  if (typeof globalThis === "undefined") {
					var getGlobal = function getGlobal() {
					  if (typeof self !== "undefined") {
						return self;
					  }
					  if (typeof window !== "undefined") {
						return window;
					  }
					  return null;
					};
					var global = getGlobal();
					if (global) {
					  Object.defineProperty(global, "globalThis", {
						value: global,
						configurable: true
					  });
					}
				  }
				})();
				window.rudderAnalyticsAddScript("".concat(sdkBaseUrl, "/").concat(sdkVersion, "/").concat(window.rudderAnalyticsBuildType, "/").concat(sdkFileName), "data-rsa-write-key", "${config.rudderstack["writeKey"]}");
			  };
			  if (typeof Promise === "undefined" || typeof globalThis === "undefined") {
				window.rudderAnalyticsAddScript("https://polyfill-fastly.io/v3/polyfill.min.js?version=3.111.0&features=Symbol%2CPromise&callback=rudderAnalyticsMount");
			  } else {
				window.rudderAnalyticsMount();
			  }
			  var loadOptions = {};
			  rudderanalytics.load("${config.rudderstack["writeKey"]}", "${config.rudderstack["dataPlaneUrl"]}", loadOptions);
			}
		  }
		})()`;
		const rudderstackScript: Script = this.scriptManagerService.createScript("rudderstackScript", scriptToAdd);
		this.scriptManagerService.loadScript(rudderstackScript);
	}

	// Disable
	private disableRudderstack(): void {
		this.scriptManagerService.removeScriptByName("rudderstackScript");
	}

	/*
		Finance Ads
 	*/
	private enableFinanceAds(): void {
		const jsCode = `window._fa=window._fa||[];window._fa.push({id:"OyCkAkzwJOc8Gw2AUwuC"});(function(d){var s=d.createElement("script");s.async=true;s.src=(d.location.protocol==="https:"?"https:":"http:")+"//www.fndsda.net/beige.js";var a=d.getElementsByTagName("script")[0];a.parentNode.insertBefore(s,a)}(document));`;
		const script: Script = this.scriptManagerService.createScript(
			"financeAds",
			jsCode,
			"id",
			"finance-ads-container"
		);
		this.scriptManagerService.loadScript(script);
	}

	private disableFinanceAds(): void {
		this.scriptManagerService.removeScriptByName("financeAds");
	}

	// Generic
	// Add
	private createScriptElementDOM(
		// TODO: eslint fix
		/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
		elem: any,
		script: string,
		htmlTag: string = "script",
		container: string = "head"
	): void {
		elem = document.createElement(htmlTag);
		elem.type = htmlTag === "script" ? "text/javascript" : "text/html";
		elem.innerHTML = script;

		// Add it as content inside <head>:
		document.getElementsByTagName(container)[0].appendChild(elem);
	}

	// Remove
	private removeScriptElementDOM(elem: Element, container: string = "head"): void {
		if (elem) {
			document.getElementsByTagName(container)[0].removeChild(elem);
		}
	}

	// Advertisement cookies Group
	private enableAdvertisementCookies(): void {
		CustomModal.injector.get(Angulartics2GoogleTagManager).eventTrack("enable_advertisement_cookies", {
			category: "advertisement_modifier",
			label: "enable",
			value: 1
		});

		this.setAdvertisementGoogleConsent("granted");
		this.enableFinanceAds();
	}

	private disableAdvertisementCookies(): void {
		CustomModal.injector.get(Angulartics2GoogleTagManager).eventTrack("disable_advertisement_cookies", {
			category: "advertisement_modifier",
			label: "disable",
			value: 0
		});

		this.setAdvertisementGoogleConsent("denied");
		this.disableFinanceAds();
	}

	public getState(): CookieBannerState {
		return this.state.getValue();
	}

	public setState(state: CookieBannerState): void {
		this.state.next(state);
	}

	public getStateObs(): Observable<CookieBannerState> {
		return this.state.asObservable();
	}
}

export class CookieBannerState {
	private essentials: boolean = false;
	private performance: boolean = false;
	private advertisement: boolean = false;
	private visibility: boolean = false;

	public setEssentials(value: boolean): void {
		this.essentials = value;
	}

	public getEssentials(): boolean {
		return this.essentials;
	}

	public setPerformance(value: boolean): void {
		this.performance = value;
	}

	public getPerformance(): boolean {
		return this.performance;
	}

	public setAdvertisement(value: boolean): void {
		this.advertisement = value;
	}

	public getAdvertisement(): boolean {
		return this.advertisement;
	}

	public isVisible(): boolean {
		return this.visibility;
	}

	public setVisibility(value: boolean): void {
		this.visibility = value;
	}
}
